import React from 'react'
import lockImage from 'direct_quote_request/lock.png'
import passwordlessImage from 'passwordless.png'

const maskPhoneForSms = (phone) => {
  if (!phone) return null

  const digits = phone.replace(/\D/g, '')
  if (digits.length < 4) return phone

  const local = digits.startsWith('61') ? digits.slice(2) : digits
  const lastFour = local.slice(-4)

  return `+61 *****${lastFour.charAt(0)} ${lastFour.slice(1)}`
}

const LOCK_ILLUSTRATION_SRC = lockImage
const NO_PASSWORD_ILLUSTRATION_SRC = passwordlessImage

const PanelIllustration = ({ noPasswordAccount }) => (
  <img
    src={noPasswordAccount ? NO_PASSWORD_ILLUSTRATION_SRC : LOCK_ILLUSTRATION_SRC}
    alt=""
    className={noPasswordAccount ? 'h-[72px] w-[72px] object-contain' : 'h-[84px] w-[84px] object-contain'}
    aria-hidden="true"
  />
)

const PanelCloseButton = ({ onClick, disabled }) => (
  <button
    type="button"
    className="absolute top-4 right-3 p-1 text-base-gray-text-secondary hover:text-base-gray-text-main disabled:opacity-50"
    onClick={onClick}
    disabled={disabled}
    aria-label="Close panel"
  >
    <hero-icon-solid name="x" class="h-4 w-4 text-base-gray-text-secondary" />
  </button>
)

const OrDivider = () => (
  <div className="flex w-full items-center gap-2">
    <div className="flex-1 border-t border-base-border" />
    <span className="text-sm font-medium text-base-gray-text-secondary">OR</span>
    <div className="flex-1 border-t border-base-border" />
  </div>
)

const SuccessAlert = ({ message }) => (
  <div className="w-full p-3 bg-base-green-50 rounded-5 flex items-center self-stretch">
    <div className="flex flex-1 items-center gap-2">
      <hero-icon-outline name="check-circle" class="h-6 w-6 text-base-green-900 flex-shrink-0" />
      <p className="text-sm font-medium text-base-green-900">{message}</p>
    </div>
  </div>
)

const DangerAlert = ({ message }) => (
  <div className="w-full p-3 bg-base-red-50 rounded-5 flex items-center gap-2 self-stretch">
    <div className="flex-1 flex justify-start items-center gap-2">
      <hero-icon-outline name="exclamation-circle" class="h-6 w-6 text-base-red-900 flex-shrink-0" />
      <p className="text-sm font-medium text-base-red-900">{message}</p>
    </div>
  </div>
)

const PasswordField = ({
  id,
  label,
  value,
  showValue,
  onToggleVisibility,
  onChange,
  autoComplete,
  hasError = false,
}) => (
  <div className="w-full space-y-1">
    <label
      className={[
        'text-sm font-medium',
        hasError ? 'text-base-red-900' : 'text-base-gray-text-main',
      ].join(' ')}
      htmlFor={id}
    >
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        type={showValue ? 'text' : 'password'}
        className={[
          'input-text-base w-full h-[42px] text-sm font-normal bg-surface pr-10',
          hasError ? 'border-2 border-base-red-main focus:border-base-red-main' : '',
        ].join(' ')}
        value={value}
        autoComplete={autoComplete}
        onChange={onChange}
      />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-base-gray-text-secondary"
        onClick={onToggleVisibility}
        tabIndex={-1}
        aria-label={showValue ? 'Hide password' : 'Show password'}
      >
        <span className={showValue ? 'hidden' : 'inline-flex'} aria-hidden={showValue}>
          <hero-icon-solid name="eye" class="w-4 h-4" />
        </span>
        <span className={showValue ? 'inline-flex' : 'hidden'} aria-hidden={!showValue}>
          <hero-icon-solid name="eye-off" class="w-4 h-4" />
        </span>
      </button>
    </div>
  </div>
)

export default class PasswordlessAuthPanel extends React.Component {
  renderOptionsView() {
    const {
      loadingEmailOtp,
      loadingSmsOtp,
      validMobileNumber,
      maskedPhone,
      onSendEmail,
      onSendSms,
      onClose,
      loading,
      noPasswordAccount,
    } = this.props

    return (
      <div className="w-full flex flex-col items-stretch gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <PanelIllustration noPasswordAccount={noPasswordAccount} />
          {noPasswordAccount ? (
            <p className="w-full text-base font-medium text-base-gray-text-main">
              Looks like you haven&apos;t created a password yet. Create a password to complete your account setup.
            </p>
          ) : (
            <p className="w-full text-base font-medium text-base-gray-text-main">
              Can&apos;t remember your password?
              <br />
              Get a link to reset your password.
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn-blue-main w-full"
          disabled={loadingEmailOtp || loading}
          onClick={onSendEmail}
        >
          <span>
            Get a code via &quot;<span className="font-extrabold">Email</span>&quot;
          </span>
        </button>
        {validMobileNumber && maskedPhone && (
          <OrDivider />
        )}
        {validMobileNumber && maskedPhone && (
          <div className="w-full flex flex-col gap-2 text-center">
            <p className="text-sm font-normal text-base-gray-text-main">
              Send an SMS to {maskedPhone}
            </p>
            <button
              type="button"
              className="btn-outline-blue-main w-full"
              disabled={loadingSmsOtp || loading}
              onClick={onSendSms}
            >
              <span>
                Get a code via &quot;<span className="font-extrabold">SMS</span>&quot;
              </span>
            </button>
          </div>
        )}
        <PanelCloseButton onClick={onClose} disabled={loading} />
      </div>
    )
  }

  renderOtpView() {
    const {
      passwordlessOtp,
      otpError,
      infoMessage,
      loadingSubmit,
      loadingEmailOtp,
      loadingSmsOtp,
      validMobileNumber,
      maskedPhone,
      lastOtpMethod,
      passwordlessResendCooldownSecondsRemaining,
      canResendPasswordlessOtp,
      onOtpChange,
      onSubmitOtp,
      onResendEmail,
      onResendSms,
      onSendSms,
      onClose,
      loading,
    } = this.props

    const isBusy = loading || loadingSubmit || loadingEmailOtp || loadingSmsOtp
    const canSubmit = passwordlessOtp.trim().length > 0 && !isBusy
    const hasOtpError = Boolean(otpError)
    const sentViaSms = lastOtpMethod === 'sms'
    const showSmsAlternative = validMobileNumber && maskedPhone && !sentViaSms
    const resendCooldownSecondsRemaining = passwordlessResendCooldownSecondsRemaining || 0
    const canResend = canResendPasswordlessOtp && !isBusy
    const emailResendLabel = resendCooldownSecondsRemaining > 0
      ? `Send again in ${resendCooldownSecondsRemaining}s`
      : (loadingEmailOtp ? 'Sending...' : 'Send again')
    const smsResendLabel = resendCooldownSecondsRemaining > 0
      ? `Send again in ${resendCooldownSecondsRemaining}s`
      : (loadingSmsOtp ? 'Sending...' : 'Send again')

    return (
      <div className="w-full flex flex-col items-stretch gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <PanelIllustration noPasswordAccount={false} />
          <p className="w-full text-base font-medium text-base-gray-text-main">
            Can&apos;t remember your password?
            <br />
            Get a link to reset your password.
          </p>
        </div>
        {infoMessage && <SuccessAlert message={infoMessage} />}
        <div className="w-full flex flex-col gap-4 pb-6">
          <div className="space-y-1">
            <label
              className={[
                'text-sm font-medium',
                hasOtpError ? 'text-base-red-900' : 'text-base-gray-text-main',
              ].join(' ')}
              htmlFor="dqr-passwordless-otp"
            >
              Enter code
            </label>
            <input
              id="dqr-passwordless-otp"
              type="text"
              inputMode="numeric"
              className={[
                'input-text-base w-full h-[42px] text-sm font-normal bg-surface',
                hasOtpError ? 'border-2 border-base-red-main focus:border-base-red-main' : '',
              ].join(' ')}
              placeholder="0000"
              value={passwordlessOtp}
              autoComplete="one-time-code"
              disabled={isBusy}
              onChange={onOtpChange}
            />
            {hasOtpError && (
              <p className="text-sm font-normal text-base-red-900">{otpError}</p>
            )}
          </div>
          <div className="space-y-2">
            <button
              type="button"
              className="btn-green-main w-full"
              disabled={!canSubmit}
              onClick={onSubmitOtp}
            >
              {loadingSubmit ? 'Submitting...' : 'Submit'}
            </button>
            {sentViaSms ? (
              <p className="text-center text-sm font-normal text-base-gray-text-main">
                Didn&apos;t receive an SMS?{' '}
                <button
                  type="button"
                  className={[
                    'font-extrabold underline',
                    canResend ? 'text-base-link' : 'text-base-gray-text-secondary cursor-not-allowed',
                  ].join(' ')}
                  disabled={!canResend}
                  onClick={onResendSms}
                >
                  {smsResendLabel}
                </button>
              </p>
            ) : (
              <p className="text-center text-sm font-normal text-base-gray-text-main">
                Didn&apos;t receive an email?{' '}
                <button
                  type="button"
                  className={[
                    'font-extrabold underline',
                    canResend ? 'text-base-link' : 'text-base-gray-text-secondary cursor-not-allowed',
                  ].join(' ')}
                  disabled={!canResend}
                  onClick={onResendEmail}
                >
                  {emailResendLabel}
                </button>
              </p>
            )}
          </div>
        </div>
        {showSmsAlternative && (
          <OrDivider />
        )}
        {showSmsAlternative && (
          <div className="w-full flex flex-col gap-2 text-center">
            <p className="text-sm font-normal text-base-gray-text-main">
              Send an SMS to {maskedPhone}
            </p>
            <button
              type="button"
              className="btn-outline-blue-main w-full"
              disabled={loading || loadingSubmit || loadingSmsOtp}
              onClick={onSendSms}
            >
              <span>
                Get a code via &quot;<span className="font-extrabold">SMS</span>&quot;
              </span>
            </button>
          </div>
        )}
        <PanelCloseButton onClick={onClose} disabled={isBusy} />
      </div>
    )
  }

  renderCreatePasswordView() {
    const {
      createPasswordError,
      newPassword,
      confirmPassword,
      showNewPassword,
      showConfirmPassword,
      onNewPasswordChange,
      onConfirmPasswordChange,
      onToggleNewPassword,
      onToggleConfirmPassword,
    } = this.props

    const hasError = Boolean(createPasswordError)

    return (
      <div className="w-full flex flex-col gap-4 items-stretch">
        <p className="text-xl font-extrabold text-base-gray-text-main text-center">
          Create your password
        </p>
        {hasError && <DangerAlert message={createPasswordError} />}
        <PasswordField
          id="dqr-new-password"
          label="New password"
          value={newPassword}
          showValue={showNewPassword}
          hasError={hasError}
          onToggleVisibility={onToggleNewPassword}
          onChange={onNewPasswordChange}
          autoComplete="new-password"
        />
        <PasswordField
          id="dqr-confirm-password"
          label="Confirm new password"
          value={confirmPassword}
          showValue={showConfirmPassword}
          hasError={hasError}
          onToggleVisibility={onToggleConfirmPassword}
          onChange={onConfirmPasswordChange}
          autoComplete="new-password"
        />
      </div>
    )
  }

  render() {
    const { view } = this.props

    return (
      <div className="relative w-full px-3 py-4 bg-base-gray-10 rounded-5 flex flex-col items-stretch gap-4">
        {view === 'options' && this.renderOptionsView()}
        {view === 'otp' && this.renderOtpView()}
        {view === 'create_password' && this.renderCreatePasswordView()}
      </div>
    )
  }
}

export { maskPhoneForSms }
